import { forwardRef, useState, useEffect, useCallback, useMemo, useRef, useImperativeHandle } from 'react';
import { isEqual, isBoolean, take } from 'lodash';

//* HOC's
import withUiContext from '@context/consumerHOC/UIConsumer';
import withLanguageContext from '@context/consumerHOC/LanguageConsumer';

//* Helpers
import formValidation from '@helpers/formValidation';

//* Components
import Button from '../Button';
import Input from './formElements/Input';
import Select from './formElements/Select';
import Checkbox from './formElements/Checkbox';
import Textarea from './formElements/Textarea';
import TagsInput from './formElements/TagsInput';
import Datepicker from './formElements/Datepicker';
import Timepicker from './formElements/Timepicker';
import DropdownCheckbox from './formElements/DropdownCheckbox';
import Text from '../Text';

//* Style
import FormStyle from './style';

const Form = forwardRef((props, ref) => {
	//! Refs
	const rowRef = useRef([]);

	//! Setting Default Values
	const setDefaultValues = useCallback(() => {
		let initialFields = {};

		const arrTypes = ['tagsInput', 'checkbox', 'dropdownCheckbox', 'select'];

		Object.keys(props.fields).map((name) => {
			const defVal = arrTypes.includes(props.fields[name].fieldType) ? [] : '';

			initialFields = {
				...initialFields,

				[name]: {
					...props.fields[name],
					errorMsg: '',
					value: props.fields[name].props?.value || defVal,
				},
			};
		});

		return initialFields;
	}, [props]);

	//! Send Function To Parent Component
	useImperativeHandle(ref, () => ({
		resetFields() {
			reset();
		},

		submitForm() {
			submitForm();
		},

		rowRefs: rowRef.current,
	}));

	//! States
	const [fields, setFields] = useState(setDefaultValues);
	const [elements, setElements] = useState([]);
	const [error, setError] = useState(null);

	//! Reset Fields
	const reset = useCallback(
		(e) => {
			e && e.preventDefault();
			updateErrorState(null);
			setFields(setDefaultValues());
		},
		[props]
	);

	//! Field Values for Listen onChange when Mounting Child Components ↓
	let fieldValues = fields;

	//! Element Value Change ⮃
	const onChange = (name, val) => {
		if ((typeof val !== 'object' && fields[name].value !== val) || (val && typeof val !== 'string' && !isEqual(fields[name].value.sort(), val.sort()))) {
			//* Syntax for Safari ---> spread operator don't work correctly in safari

			let newObj = { ...fieldValues };

			newObj[name] = { ...fields[name], value: val };

			fieldValues = newObj;

			setFields(newObj);

			const [formValues, newError] = validateForm(newObj, true);

			//* Checking is now value is equal form starting value
			!newError && isEqual(formOldValues, formValues) ? updateErrorState(null) : updateErrorState(newError);

			//* Calling parent change
			props.onChange && props.onChange(name, val, formValues);
		}
	};

	//! Element Key Down
	const keyDown = (e) => {
		// Your Code Here
	};

	//! Form Submit
	const submitForm = (e) => {
		e && e.preventDefault();

		const [formValues, newError, newVals] = validateForm();

		updateErrorState(newError);

		if (newError === false) {
			props.submit && props.submit(formValues);
		} else {
			setFields(newVals);
		}
	};

	//! Update Error State
	const updateErrorState = (newError) => {
		if (newError) {
			!error && setError(true);
		} else {
			setError(newError);
		}
	};

	//! Validate Form Element
	const validateFormEl = (value, type, settings) => {
		return formValidation(value, type, '', settings);
	};

	//! Validate Form
	const validateForm = (newFields, getValues = false) => {
		let newError = false,
			newVals = newFields || fields,
			formValues = {};

		Object.keys(newVals).map((name) => {
			formValues = { ...formValues, [name]: newVals[name].value };

			if (newVals[name].props?.required) {
				let value = newVals[name].value;

				if (newVals[name].props.type === 'c_password') {
					value = { pass: newVals.password.value, c_pass: newVals[name].value };
				}

				let settings = !isBoolean(newVals[name].props.required) && newVals[name].props.required;

				const isValid = validateFormEl(value, newVals[name].props.type || newVals[name].fieldType, settings);

				let msg = '';

				if (!isBoolean(isValid)) {
					newError = true;
					msg = !getValues && isValid;
				}

				//* Syntax for Safari ---> spread operator don't work correctly in safari
				let newObj = { ...newVals };
				newObj[name] = { ...newVals[name], errorMsg: msg };
				newVals = newObj;
			}
		});

		return [formValues, newError, newVals];
	};

	const [formOldValues] = useMemo(() => validateForm(setDefaultValues(), true), [props]);

	//! Returning Elements From Type
	const getElement = useCallback(
		(name, field) => {
			switch (field.fieldType) {
				case 'input':
					return (
						<Input
							name={name}
							keyDown={keyDown}
							parentError={error}
							parentVal={field.value}
							errorMsg={field.errorMsg}
							className={props.className}
							inputWrapClass={props.inputWrapClass}
							realTimeValidate={props.realTimeValidate}
							onChange={(name, val) => onChange(name, val)}
							password={field.props.type === 'c_password' && fields.password.value}
							{...field.props}
						/>
					);
				case 'tagsInput':
					return (
						<TagsInput
							name={name}
							parentError={error}
							parentVal={field.value}
							errorMsg={field.errorMsg}
							realTimeValidate={props.realTimeValidate}
							onChange={(name, val) => onChange(name, val)}
							{...field.props}
						/>
					);
				case 'checkbox':
					return (
						<Checkbox
							name={name}
							type='checkbox'
							parentError={error}
							parentVal={field.value}
							errorMsg={field.errorMsg}
							className={field.checkboxClassName}
							realTimeValidate={props.realTimeValidate}
							onChange={(name, val) => onChange(name, val)}
							{...field.props}
						/>
					);
				case 'dropdownCheckbox':
					return (
						<DropdownCheckbox
							name={name}
							type='checkbox'
							parentError={error}
							parentVal={field.value}
							errorMsg={field.errorMsg}
							realTimeValidate={props.realTimeValidate}
							onChange={(name, val) => onChange(name, val)}
							{...field.props}
						/>
					);
				case 'textarea':
					return (
						<Textarea
							name={name}
							keyDown={keyDown}
							parentError={error}
							parentVal={field.value}
							errorMsg={field.errorMsg}
							className={field.textareaClassName}
							realTimeValidate={props.realTimeValidate}
							onChange={(name, val) => onChange(name, val)}
							{...field.props}
						/>
					);
				case 'select':
					return (
						<Select
							name={name}
							keyDown={keyDown}
							parentError={error}
							parentVal={field.value}
							errorMsg={field.errorMsg}
							realTimeValidate={props.realTimeValidate}
							onChange={(name, val) => onChange(name, val)}
							{...field.props}
						/>
					);
				case 'datepicker':
					return (
						<Datepicker
							name={name}
							parentError={error}
							parentVal={field.value}
							errorMsg={field.errorMsg}
							onChange={(name, val) => onChange(name, val)}
							{...field.props}
						/>
					);
				case 'timepicker':
					return (
						<Timepicker
							name={name}
							parentError={error}
							parentVal={field.value}
							errorMsg={field.errorMsg}
							onChange={(name, val) => onChange(name, val)}
							date={field.props.datepickerName && fields[field.props.datepickerName].value}
							{...field.props}
						/>
					);
				default:
					return;
			}
		},
		[fields, error, props]
	);

	//! Fields to Row
	const fieldsToRow = useCallback(
		(elementsList) => {
			return props.sizes.map((c, k) => {
				const pulled = take(elementsList, c);
				elementsList = elementsList.slice(c);

				return props.rowTitle ? (
					<div
						key={k}
						className={props.rowTitleContClassName}>
						{props.rowTitle && <div className={props.rowTitleClassName}>{props.translate(props.rowTitle[k])}</div>}
						<div className='form-row'>{pulled}</div>
					</div>
				) : (
					<div
						key={k}
						className='form-row'>
						{pulled}
					</div>
				);
			});
		},
		[props.sizes, props.rowTitle, props.rowTitleClassName]
	);

	//! Returning All Elements with Titles and Subtitles
	const getElements = useCallback(() => {
		let elementsList = Object.keys(fields).map((name, i) => {
			return (
				<div
					className={`form-col ${props.wrapContClass || ''}`}
					key={i}
					ref={(ref) => (rowRef.current[i] = ref)}>
					{getElement(name, fields[name])}
				</div>
			);
		});

		if (props.sizes) {
			return fieldsToRow(elementsList);
		}

		return elementsList;
	}, [fields, props.sizes, error, rowRef]);

	//! Getting Fields and Setting All Elements
	useEffect(() => {
		setElements(getElements());
	}, [fields, error, props.sizes]);

	return (
		<FormStyle
			onSubmit={submitForm}
			className={`${error ? 'form-error' : ''}`}
			autoComplete='off'
			ref={ref}>
			<div className='form-rows'>{elements}</div>

			{props.formError.error && (
				<div className='form-error-msg-cont'>
					{typeof props.formError.msg === 'string' ? (
						<Text
							tag={'span'}
							className='form-error-msg'>
							{props.formError.msg}
						</Text>
					) : (
						Object.values(props.formError.msg).map((m, k) => (
							<Text
								tag={'span'}
								key={k}
								className='form-error-msg'>
								{m}
							</Text>
						))
					)}
				</div>
			)}

			{(props.submitText || props.reset) && (
				<div className='btn-actions'>
					{props.submitText && (
						<Button
							teamFormBtn={props.teamFormBtn}
							disabled={!!props.disabledBtn && error !== false}
							onClick={submitForm}
							text={props.translate(props.submitText)}
						/>
					)}

					{props.reset && (
						<Button
							onClick={reset}
							text={props.translate(props.resetText || 'reset')}
						/>
					)}
				</div>
			)}
		</FormStyle>
	);
});

Form.displayName = 'CustomForm';

export default withLanguageContext(withUiContext(Form, ['formError']), ['translate']);
